<template>
    <div class="main-wrapper open-challenges-page">
        <layout-header />
        <div class="content container challenge-shell">
            <div class="px-3">
                <div class="row">
                    <div class="col-lg-3 d-lg-block d-none theiaStickySidebar mb-3">
                        <div class="stickyside">
                            <div class="rounded-xl d-none d-lg-block shadow-sm p-2 bg-white challenge-sidebar-card"><customer-sidebar /></div>
                        </div>
                    </div>
                    <div class="col-lg-9 col-md-12 mb-5">
                        <div class="challenge-hero mb-3">
                            <div class="d-flex justify-content-between align-items-start flex-wrap">
                                <div class="mb-3 mr-3">
                                    <h2 class="mb-1 challenge-title">Défis publics</h2>
                                    <p class="text-muted text-sm mb-0">Consultez les terrains ouverts aux défis et gérez les demandes reçues.</p>
                                </div>
                                <div v-if="activeTab === 'opened'" class="challenge-team-card card shadow-sm border-0 ml-auto">
                                    <div class="card-body p-3">
                                        <div class="d-flex justify-content-between align-items-center mb-2">
                                            <span class="text-sm font-weight-bold">Mon équipe</span>
                                            <span class="badge badge-pill bg-light text-dark">Envoyer un défi</span>
                                        </div>
                                        <select v-model="selectedTeamId" class="form-control form-control-sm custom-select mb-2">
                                            <option :value="null">Choisir une équipe...</option>
                                            <option v-for="team in challengeTeams" :key="team.id" :value="team.id">{{ team.name }}</option>
                                        </select>
                                        <div v-if="challengeTeams.length === 0" class="text-xs text-danger">
                                            Vous devez être capitaine ou propriétaire d'une équipe pour envoyer un défi.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card shadow-sm border-0 mb-3 filter-panel">
                            <div class="card-body p-3 p-md-4">
                                <div class="d-flex justify-content-center align-items-center flex-wrap mb-3 challenge-tabs">
                                    <a v-for="tab in tabs" :key="tab.id" @click="loadTab(tab.id)"
                                        :class="{ 'bg-warning-light': activeTab === tab.id, 'ml-1': tab.id !== tabs[0].id }"
                                        href="javascript:void(0)" class="position-relative badge badge-pill text-dark bg-light text-xs py-3 px-3">
                                        <span class="px-1">{{ tab.label }}</span>
                                        <span v-if="tab.count > 0"
                                            class="badge badge-dark text-white position-absolute mr-2 mt-2 p-1 rounded-xl challenge-tab-count"
                                            style="min-width: 20px; top:0; right:0; transform: translate(50%, -50%);">
                                            {{ tab.count }}
                                        </span>
                                    </a>
                                </div>
                                <div v-if="activeTab === 'opened'" class="row">
                                    <div class="col-md-6 mb-2 mb-md-0">
                                        <label class="text-xs text-muted mb-1">Délégation</label>
                                        <select v-model="selectedDelegation" class="form-control form-control-sm custom-select">
                                            <option :value="null">Toutes les délégations</option>
                                            <option v-for="delegation in availableDelegations" :key="delegation" :value="delegation">{{ delegation }}</option>
                                        </select>
                                    </div>
                                    <div class="col-md-6">
                                        <label class="text-xs text-muted mb-1">Établissement</label>
                                        <select v-model="selectedEstablishmentId" class="form-control form-control-sm custom-select">
                                            <option :value="null">Tous les établissements</option>
                                            <option v-for="establishment in availableEstablishments" :key="establishment.id" :value="String(establishment.id)">{{ establishment.business_name }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="loading" class="text-center py-4">
                            <span class="spinner-border spinner-border-sm mr-2"></span>Chargement...
                        </div>

                        <template v-else>
                            <div v-if="pageError" class="alert alert-danger border-0 shadow-sm mb-3 d-flex justify-content-between align-items-center">
                                <span>{{ pageError }}</span>
                                <button type="button" class="btn btn-link btn-sm text-danger p-0" @click="pageError = ''">Fermer</button>
                            </div>

                            <div v-if="activeTab === 'opened'">
                                <div v-if="filteredOpenedChallenges.length === 0" class="text-center py-4 text-muted">
                                    Aucun défi public disponible pour le moment.
                                </div>

                                <div v-else class="row challenge-grid">
                                    <div v-for="booking in filteredOpenedChallenges" :key="booking.id" class="col-md-6 mb-3">
                                        <div class="card challenge-card h-100 shadow-sm border-0">
                                            <div class="card-body d-flex flex-column">
                                                <div class="d-flex justify-content-between align-items-start mb-3">
                                                    <div>
                                                        <span class="badge badge-pill bg-primary-light challenge-pill">Ouvert aux défis</span>
                                                        <div class="text-xs text-muted mt-2">{{ formatDate(booking.start_time) }}</div>
                                                    </div>
                                                    <div class="text-right">
                                                        <div :class="booking.status === 'confirmed' ? 'bg-success-light' : 'bg-warning-light'" class="badge badge-pill text-xs py-1 px-2 mb-2">
                                                            {{ booking.status === 'confirmed' ? 'confirmée' : 'en attente de confirmation' }}
                                                        </div>
                                                        <div class="text-xs text-muted">{{ formatTime(booking.start_time) }} - {{ formatTime(booking.end_time) }}</div>
                                                    </div>
                                                </div>

                                                <h5 class="mb-1 challenge-card-title">{{ booking.challenge_team?.name }}</h5>
                                                <div class="text-sm mb-2 text-muted">
                                                    <b class="text-dark">{{ booking.slot?.facility?.establishment?.business_name }}</b> — Terrain {{ booking.slot?.facility?.name }}
                                                </div>
                                                <div class="challenge-card-meta d-flex align-items-center mb-3">
                                                    <span class="material-symbols-outlined text-muted mr-2" style="font-size: 18px;">location_on</span>
                                                    <span class="text-xs text-muted">{{ booking.slot?.facility?.establishment?.delegation }}</span>
                                                </div>

                                                <div class="mt-auto d-flex justify-content-between align-items-center flex-wrap">
                                                    <span class="text-xs text-muted mb-2 mb-md-0">Défi public disponible</span>
                                                    <button class="btn btn-primary btn-sm challenge-action"
                                                        :disabled="!selectedTeamId || isOwnChallenge(booking) || sendingId === booking.id || booking.status !== 'confirmed'"
                                                        @click="confirmSend(booking)">
                                                        <span v-if="sendingId === booking.id" class="spinner-border spinner-border-sm mr-1"></span>
                                                        {{ isOwnChallenge(booking) ? 'Votre réservation' : 'Envoyer un défi' }}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>  

                            <div v-else>
                                <div v-if="requests.length === 0" class="text-center py-4 text-muted">
                                    Aucune demande en attente.
                                </div>

                                <div v-else class="row">
                                    <div v-for="match in requests" :key="match.id" class="col-lg-6 mb-3">
                                        <div class="card h-100 shadow-sm border-0">
                                            <div class="card-body d-flex flex-column">
                                                <div class="d-flex justify-content-between align-items-start mb-2">
                                                    <span class="badge badge-pill bg-warning-light">Demande reçue</span>
                                                    <span class="text-xs text-muted">{{ formatDate(match.booking?.start_time) }}</span>
                                                </div>
                                                <h5 class="mb-1"><b>{{ match.team_a?.name }}</b> vs <b>{{ match.team_b?.name }}</b></h5>
                                                <div class="text-sm mb-1">
                                                    {{ match.booking?.slot?.facility?.establishment?.business_name }} — Terrain {{ match.booking?.slot?.facility?.name }}
                                                </div>
                                                <div class="text-muted text-sm mb-3">
                                                    {{ formatTime(match.booking?.start_time) }} - {{ formatTime(match.booking?.end_time) }}
                                                </div>
                                                <div class="mt-auto d-flex justify-content-end flex-wrap">
                                                    <button class="btn btn-success btn-sm mr-2 mb-2" @click="confirmRespond(match, 'accepted')">Accepter</button>
                                                    <button class="btn btn-outline-danger btn-sm mb-2" @click="confirmRespond(match, 'declined')">Refuser</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <layout-footer />
    </div>
</template>

<script>
import moment from 'moment'
import { api } from '@/store'
import CustomerSidebar from '@/components/Customers/Partials/Sidebar'

export default {
    components: { 'customer-sidebar': CustomerSidebar },
    data() {
        return {
            openedChallenges: [],
            requests: [],
            challengeTeams: [],
            selectedTeamId: null,
            selectedDelegation: null,
            selectedEstablishmentId: null,
            currentPlayer: null,
            loading: true,
            sendingId: null,
            pageError: '',
            activeTab: 'opened',
            tabs: [
                { id: 'opened', label: 'Défis ouverts', count: 0 },
                { id: 'requests', label: 'Demandes reçues', count: 0 },
            ],
        }
    },
    mounted() { this.load() },
    watch: {
        filteredOpenedChallenges() {
            this.refreshTabs()
        },
        requests() {
            this.refreshTabs()
        },
    },
    computed: {
        availableDelegations() {
            return [...new Set(this.openedChallenges.map(booking => booking.slot?.facility?.establishment?.delegation).filter(Boolean))].sort()
        },
        availableEstablishments() {
            const establishments = this.openedChallenges.map(booking => booking.slot?.facility?.establishment).filter(Boolean)
            const unique = new Map()
            establishments.forEach(establishment => {
                if (!unique.has(String(establishment.id))) {
                    unique.set(String(establishment.id), establishment)
                }
            })
            return [...unique.values()].sort((left, right) => (left.business_name || '').localeCompare(right.business_name || ''))
        },
        filteredOpenedChallenges() {
            return this.openedChallenges.filter(booking => {
                const delegation = booking.slot?.facility?.establishment?.delegation || null
                const establishmentId = booking.slot?.facility?.establishment?.id ? String(booking.slot.facility.establishment.id) : null
                const delegationMatch = !this.selectedDelegation || delegation === this.selectedDelegation
                const establishmentMatch = !this.selectedEstablishmentId || establishmentId === this.selectedEstablishmentId
                return delegationMatch && establishmentMatch
            })
        },
    },
    methods: {
        refreshTabs() {
            this.tabs = [
                { id: 'opened', label: 'Défis ouverts', count: this.filteredOpenedChallenges.length },
                { id: 'requests', label: 'Demandes reçues', count: this.requests.length },
            ]
        },
        async loadTab(tabId) {
            this.activeTab = tabId
            if (tabId === 'opened') {
                await this.loadOpenedChallenges()
                return
            }
            if (tabId === 'requests') {
                await this.loadRequests()
            }
        },
        async load() {
            this.loading = true
            this.pageError = ''
            try {
                const [player, teams] = await Promise.all([api.get('/private/players/me'), api.get('/private/teams')])
                this.currentPlayer = player.data
                this.challengeTeams = (teams.data || []).filter(team => team.owner_id === player.data.id || team.captain_id === player.data.id)
                await Promise.all([this.loadOpenedChallenges(), this.loadRequests()])
                this.refreshTabs()
            } catch (error) { this.showError(error) } finally { this.loading = false }
        },
        async loadOpenedChallenges() {
            const response = await api.get('/private/open-challenges')
            this.openedChallenges = response.data || []
            this.refreshTabs()
        },
        async loadRequests() {
            const response = await api.get('/private/open-challenges/requests')
            this.requests = response.data || []
            this.refreshTabs()
        },
        isOwnChallenge(booking) { return booking.challenge_team?.owner_id === this.currentPlayer?.id || booking.challenge_team?.captain_id === this.currentPlayer?.id },
        confirmSend(booking) {
            if (!this.selectedTeamId) {
                this.showError({ response: { data: { detail: 'Choisissez une équipe avant d’envoyer le défi.' } } })
                return
            }
            this.$store.dispatch('modal/triggerModal', {
                stacked: true,
                icon: 'warning',
                icon_color: 'text-warning',
                title: 'Envoyer le défi',
                detail: `Envoyer un défi à ${booking.challenge_team?.name} avec votre équipe sélectionnée ?`,
                prompt: true,
                onResult: (confirmed) => {
                    if (confirmed) {
                        this.sendRequest(booking)
                    }
                }
            })
        },
        async sendRequest(booking) {
            this.sendingId = booking.id
            this.pageError = ''
            try {
                await api.post(`/private/open-challenges/${booking.id}/request`, { team_id: this.selectedTeamId })
                this.$store.dispatch('modal/triggerModal', { stacked: true, icon: 'check_circle', icon_color: 'text-success', title: 'Défi envoyé', detail: 'Le capitaine ou propriétaire recevra votre demande.' })
                await this.loadTab('opened')
            } catch (error) { this.showError(error) } finally { this.sendingId = null }
        },
        confirmRespond(match, status) {
            this.$store.dispatch('modal/triggerModal', {
                stacked: true,
                icon: status === 'accepted' ? 'check_circle' : 'warning',
                icon_color: status === 'accepted' ? 'text-success' : 'text-warning',
                title: status === 'accepted' ? 'Accepter la demande' : 'Refuser la demande',
                detail: status === 'accepted' ? 'Confirmer cette demande de défi ?' : 'Refuser cette demande de défi ?',
                prompt: true,
                onResult: (confirmed) => {
                    if (confirmed) {
                        this.respond(match, status)
                    }
                }
            })
        },
        async respond(match, status) {
            try {
                await api.post(`/private/open-challenges/requests/${match.id}/respond`, { status })
                this.$store.dispatch('modal/triggerModal', { stacked: true, icon: status === 'accepted' ? 'check_circle' : 'info', icon_color: status === 'accepted' ? 'text-success' : 'text-muted', title: status === 'accepted' ? 'Défi accepté' : 'Défi refusé', detail: status === 'accepted' ? 'Le défi devient maintenant un match programmé.' : 'L’équipe a été informée de votre réponse.' })
                await this.loadTab('requests')
            } catch (error) { this.showError(error) }
        },
        showError(error) {
            const detail = error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.'
            this.pageError = detail
            this.$store.dispatch('modal/triggerModal', { stacked: true, icon: 'error', icon_color: 'text-danger', title: 'Erreur', detail })
        },
        formatDate(value) { return value ? moment(value).locale('fr').format('dddd D MMMM') : '' },
        formatTime(value) { return value ? moment(value).format('HH:mm') : '' },
    },
}
</script>
<style scoped>
.open-challenges-page {
    background: #fff;
}

.challenge-shell {
    padding-top: 16px;
}

.challenge-hero {
    padding: 14px 16px 0;
}

.challenge-kicker {
    display: none;
}

.challenge-title {
    font-size: 2rem;
    line-height: 1.1;
    font-weight: 800;
    letter-spacing: -0.03em;
}

.challenge-team-card {
    min-width: 320px;
    max-width: 420px;
    border-radius: 20px;
    background: #ffffff;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08) !important;
}

.challenge-sidebar-card,
.filter-panel,
.challenge-card {
    border-radius: 20px;
}

.challenge-tab-count {
    border: 2px solid #fff;
}

.challenge-grid .challenge-card {
    transition: transform .18s ease, box-shadow .18s ease;
    background: #ffffff;
}

.challenge-grid .challenge-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 34px rgba(15, 23, 42, 0.10) !important;
}

.challenge-pill {
    padding: 8px 12px;
    border-radius: 999px;
}

.challenge-card-title {
    font-size: 1.1rem;
    font-weight: 700;
    letter-spacing: -0.02em;
}

.challenge-card-meta {
    padding: 10px 12px;
    border-radius: 14px;
    background: #f8fafc;
}

.challenge-action {
    min-width: 148px;
    border-radius: 999px;
}

@media (max-width: 991.98px) {
    .challenge-team-card {
        min-width: 100%;
        max-width: 100%;
    }

    .challenge-hero {
        padding: 0;
    }

    .challenge-title {
        font-size: 1.55rem;
    }
}
</style>
